import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  Clock,
  Eye,
  MapPin,
  PencilSimple,
  Phone,
  PushPin,
  PushPinSlash,
  Trash,
  X,
} from '@phosphor-icons/react';
import { Calendar as DateCalendar } from 'react-date-range';
import { es } from 'date-fns/locale';
import { getTaskStatusColor, getTaskStatusLabel } from '../../../constants/taskColors.js';
import ActionMenu from '../../../shared/components/ui/ActionMenu.jsx';
import { useTeamLoads } from '../../../hooks/useTeamLoads.js';
import TeamLoadsBand from './team-loads/TeamLoadsBand.jsx';
import { taskHasTeam } from '../utils/taskFixedTeam.js';
import { findVisible, useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';

const UNASSIGNED_TEAM_KEY = '__unassigned_team__';
const DEFAULT_DAY_START_HOUR = 7;
const DEFAULT_DAY_END_HOUR = 20;
const EMPTY_DAY_ROW_HEIGHT = 48;
const COLLAPSED_EMPTY_ROW_HEIGHT = 36;
const POPULATED_DAY_ROW_MIN_HEIGHT = 88;
const TASK_POPOVER_WIDTH = 360;
const TASK_POPOVER_VIEWPORT_MARGIN = 12;
const WEEKDAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
const STICKY_HOUR_COLUMN_SHADOW = '-1px 0 0 rgb(var(--app-border-rgb)), 1px 0 0 rgb(var(--app-border-rgb))';

function isValidDate(value) {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

export function ensureDate(value) {
  if (isValidDate(value)) {
    return new Date(value);
  }

  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : new Date();
}

function parseYmd(value) {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  return isValidDate(parsed) ? parsed : null;
}

export function formatYmd(date) {
  const safeDate = ensureDate(date);
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');
  const day = String(safeDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getMonthGrid(referenceDate) {
  const baseDate = ensureDate(referenceDate);
  const firstDay = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const lastDay = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
  const gridStart = new Date(firstDay);
  const gridEnd = new Date(lastDay);

  gridStart.setDate(firstDay.getDate() - firstDay.getDay());
  gridEnd.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

  const days = [];
  for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor.setDate(cursor.getDate() + 1)) {
    days.push(new Date(cursor));
  }

  return days;
}

function formatHeaderDate(date, mode) {
  const safeDate = ensureDate(date);
  return safeDate.toLocaleDateString('es-AR', mode === 'month'
    ? { month: 'long', year: 'numeric' }
    : { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatHourLabel(hour) {
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = hour % 12 || 12;
  return `${normalizedHour} ${suffix}`;
}

function formatHourRangeLabel(startHour, endHour) {
  const endBoundaryHour = (endHour + 1) % 24;
  return `${formatHourLabel(startHour)} - ${formatHourLabel(endBoundaryHour)}`;
}

function formatTaskTime(value) {
  if (!value) return 'Sin horario';
  const date = ensureDate(value);
  return date.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatCoordinates(task) {
  const lat = task?.client?.lat;
  const lng = task?.client?.lng;
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return 'Sin coordenadas';
  }

  const normalizedLat = Number(lat);
  const normalizedLng = Number(lng);

  if (Number.isFinite(normalizedLat) && Number.isFinite(normalizedLng)) {
    return `${normalizedLat.toFixed(6)}, ${normalizedLng.toFixed(6)}`;
  }

  return `${lat}, ${lng}`;
}

function hasValidationReason(validateReason) {
  if (!validateReason) {
    return false;
  }

  if (typeof validateReason === 'string') {
    return validateReason.trim().length > 0;
  }

  if (typeof validateReason === 'object') {
    return Object.values(validateReason).some((value) => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }

      return value !== null && value !== undefined;
    });
  }

  return Boolean(validateReason);
}

function getTaskValidationLabel(task) {
  if (!task?.isValidated) {
    return 'Pendiente de validación';
  }

  return hasValidationReason(task?.validateReason)
    ? 'Validada con motivo'
    : 'Validada automatica';
}

function colorToRgba(hexColor, alpha) {
  if (typeof hexColor !== 'string') {
    return `rgba(59, 130, 246, ${alpha})`;
  }

  const normalized = hexColor.replace('#', '');
  const shortHex = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized.slice(0, 6);

  const value = Number.parseInt(shortHex, 16);
  if (Number.isNaN(value)) {
    return `rgba(59, 130, 246, ${alpha})`;
  }

  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function buildTaskTypeLookup(taskTypes) {
  return new Map((taskTypes || []).map((taskType) => [taskType.name, taskType]));
}

function getTaskTypeMeta(task, taskTypeLookup) {
  return taskTypeLookup.get(task?.taskTypeId) || taskTypeLookup.get(task?.taskType) || null;
}

function getTaskTypeLabel(task, taskTypeLookup, fallbackLabel = null) {
  return fallbackLabel
    || getTaskTypeMeta(task, taskTypeLookup)?.displayName
    || task?.taskTypeName
    || task?.taskTypeId
    || task?.taskType
    || 'Sin tipo';
}

function getTaskCategoryLabel(task, taskTypeLookup) {
  const categoryName = getTaskTypeMeta(task, taskTypeLookup)?.categoryName
    || task?.categoryName
    || task?.category;

  if (!categoryName) {
    return 'Sin categoría';
  }

  return String(categoryName)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDurationValue(value, unit = 'minutes') {
  const normalizedValue = Number(value);
  if (!Number.isFinite(normalizedValue) || normalizedValue <= 0) {
    return null;
  }

  if (unit === 'hours') {
    return `${normalizedValue} h`;
  }

  if (unit === 'days') {
    return `${normalizedValue} ${normalizedValue === 1 ? 'dia' : 'dias'}`;
  }

  return `${normalizedValue} min`;
}

function getTaskDurationLabel(task, taskTypeLookup) {
  if (task?.isCorporate) {
    const assignedDuration = formatDurationValue(
      task?.estimatedDuration,
      task?.isTimeInHours ? 'hours' : 'days',
    );

    return assignedDuration
      ? `Duracion asignada: ${assignedDuration}`
      : 'Duracion asignada: no informada';
  }

  const estimatedDuration = formatDurationValue(
    task?.taskTypeEstimatedDuration ?? getTaskTypeMeta(task, taskTypeLookup)?.estimatedDuration,
    'minutes',
  );

  return estimatedDuration
    ? `Duracion estimada: ${estimatedDuration}`
    : 'Duracion estimada: no informada';
}

function TaskCard({ task, variant = 'day', taskTypeLabel, taskTypeLookup, onClick }) {
  const statusColor = getTaskStatusColor(task?.status);
  const title = task?.title?.trim() || 'Sin titulo';
  const resolvedTaskTypeLabel = getTaskTypeLabel(task, taskTypeLookup, taskTypeLabel);
  const durationLabel = getTaskDurationLabel(task, taskTypeLookup);
  const containerClasses = variant === 'month'
    ? 'block w-full min-w-0 overflow-hidden rounded-lg border px-2 py-1.5 text-left transition-colors hover:brightness-110'
    : 'block w-full min-w-0 overflow-hidden rounded-xl border px-3 py-2.5 text-left transition-colors hover:brightness-110 min-h-[76px]';

  return (
    <button
      type="button"
      data-tour="cal-task-card"
      onClick={onClick}
      className={containerClasses}
      style={{
        backgroundColor: colorToRgba(statusColor, variant === 'month' ? 0.16 : 0.2),
        borderColor: colorToRgba(statusColor, 0.65),
      }}
      title={title}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <p className={`min-w-0 flex-1 break-words font-semibold text-content ${variant === 'month' ? 'text-xs leading-4' : 'text-sm leading-5'}`}>
          {title}
        </p>
        {task.fixedTeam === true && (
          <PushPin size={14} weight="fill" className="mt-0.5 shrink-0 text-accent-text" aria-label="Fijada al equipo" />
        )}
        <span
          className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: statusColor }}
          aria-hidden="true"
        />
      </div>
      <p className={`mt-1 break-words text-content ${variant === 'month' ? 'text-[11px]' : 'text-xs'}`}>
        {resolvedTaskTypeLabel}
      </p>
      <p className={`mt-1 break-words text-content-muted ${variant === 'month' ? 'text-[11px] leading-4' : 'text-xs leading-4'}`}>
        {durationLabel}
      </p>
    </button>
  );
}

export default function TasksCalendarView({
  tasks = [],
  teams = [],
  taskTypes = [],
  teamLoadsRefreshToken = 0,
  selectedTeamId = '',
  showValidation = false,
  mode = 'day',
  selectedDate = new Date(),
  onModeChange,
  onDateChange,
  loading = false,
  onViewTask,
  onEditTask,
  onDeleteTask,
  canEditTask,
  canDeleteTask,
  onToggleFixedTeam,
}) {
  const [isGroupedByTeamView, setIsGroupedByTeamView] = useState(true);
  const [openMobileTeamId, setOpenMobileTeamId] = useState(null);
  // En memoria: sobrevive al cambio de dia, al filtro y a ir a Mes y volver (este
  // componente no se desmonta), y se pierde al recargar la pantalla.
  const [isDetailExpanded, setIsDetailExpanded] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [popoverTask, setPopoverTask] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, maxHeight: 0 });
  const [miniCalendarPosition, setMiniCalendarPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);
  const miniCalendarRef = useRef(null);
  const miniCalendarButtonRef = useRef(null);
  const calendarViewportRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const floatingScrollbarRef = useRef(null);
  const floatingScrollbarTrackRef = useRef(null);
  const isSyncingHorizontalScrollRef = useRef(false);
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  // Recorrido guiado (calendarTour): en móvil despliega el primer equipo con tareas (openTeam),
  // abre el detalle de la primera tarjeta visible en sus pasos y lo cierra al salir de ellos.
  useGuidedTourStep((step) => {
    if (step?.openTeam && !openMobileTeamId) {
      setOpenMobileTeamId(findVisible('[data-tour="cal-day-mobile"] [data-team-with-tasks]')?.dataset.teamWithTasks ?? null);
    }
    if (step?.modal === 'task-detail' && !popoverTask) findVisible('[data-tour="cal-task-card"]')?.click();
    if (!step?.modal && popoverTask) setPopoverTask(null);
  });
  const [floatingScrollbarMetrics, setFloatingScrollbarMetrics] = useState({
    isVisible: false,
    left: 0,
    width: 0,
  });

  const safeSelectedDate = useMemo(() => ensureDate(selectedDate), [selectedDate]);
  const selectedDayKey = useMemo(() => formatYmd(safeSelectedDate), [safeSelectedDate]);
  const todayKey = useMemo(() => formatYmd(new Date()), []);
  const taskTypeLookup = useMemo(() => buildTaskTypeLookup(taskTypes), [taskTypes]);

  useEffect(() => {
    setOpenMobileTeamId(null);
  }, [selectedDayKey]);
  const knownTeamIds = useMemo(() => new Set(teams.map((team) => team.id)), [teams]);
  const selectedDayTasks = useMemo(() => {
    return tasks
      .filter((task) => {
        if (!task?.scheduledDate) return false;
        return formatYmd(ensureDate(task.scheduledDate)) === selectedDayKey;
      })
      .sort((leftTask, rightTask) => {
        return new Date(leftTask.scheduledDate).getTime() - new Date(rightTask.scheduledDate).getTime();
      });
  }, [selectedDayKey, tasks]);
  const hasUnassignedTasksInSelectedDay = useMemo(() => {
    return selectedDayTasks.some((task) => {
      return !task.teamId || !knownTeamIds.has(task.teamId);
    });
  }, [knownTeamIds, selectedDayTasks]);

  const orderedTeams = useMemo(() => {
    const sortedTeams = [...teams].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', {
      sensitivity: 'base',
    }));

    return hasUnassignedTasksInSelectedDay
      ? [...sortedTeams, { id: UNASSIGNED_TEAM_KEY, name: 'Sin equipo' }]
      : sortedTeams;
  }, [hasUnassignedTasksInSelectedDay, teams]);

  const visibleTeams = useMemo(() => {
    if (!selectedTeamId) {
      return orderedTeams;
    }

    const selectedTeam = orderedTeams.find((team) => team.id === selectedTeamId);
    return selectedTeam ? [selectedTeam] : orderedTeams;
  }, [orderedTeams, selectedTeamId]);

  // La banda de resumen solo existe en modo Dia con la sub-vista agrupada por equipos,
  // y solo si hay columnas de equipo: fuera de ahi no se renderiza ni se pide el dato.
  const isTeamLoadsBandVisible = mode === 'day' && isGroupedByTeamView && visibleTeams.length > 0;

  const {
    loadsByTeamId,
    loading: isLoadingTeamLoads,
    error: teamLoadsError,
    refetch: refetchTeamLoads,
  } = useTeamLoads(selectedDayKey, {
    enabled: isTeamLoadsBandVisible,
    refreshToken: teamLoadsRefreshToken,
  });

  const tasksByMonthDay = useMemo(() => {
    return tasks.reduce((accumulator, task) => {
      if (!task?.scheduledDate) return accumulator;

      const taskDate = ensureDate(task.scheduledDate);
      const taskKey = formatYmd(taskDate);

      if (!accumulator[taskKey]) {
        accumulator[taskKey] = [];
      }

      accumulator[taskKey].push(task);
      return accumulator;
    }, {});
  }, [tasks]);

  const dayBuckets = useMemo(() => {
    const buckets = {};

    selectedDayTasks.forEach((task) => {
      const taskDate = ensureDate(task.scheduledDate);
      const taskHour = taskDate.getHours();
      const teamKey = task.teamId && knownTeamIds.has(task.teamId)
        ? task.teamId
        : UNASSIGNED_TEAM_KEY;

      if (!buckets[taskHour]) {
        buckets[taskHour] = {};
      }

      if (!buckets[taskHour][teamKey]) {
        buckets[taskHour][teamKey] = [];
      }

      buckets[taskHour][teamKey].push(task);
    });

    Object.values(buckets).forEach((bucket) => {
      Object.values(bucket).forEach((bucketTasks) => {
        bucketTasks.sort((leftTask, rightTask) => {
          return new Date(leftTask.scheduledDate).getTime() - new Date(rightTask.scheduledDate).getTime();
        });
      });
    });

    return buckets;
  }, [knownTeamIds, selectedDayTasks]);

  const dayTasksByTeam = useMemo(() => {
    return selectedDayTasks.reduce((accumulator, task) => {
      const teamKey = task.teamId && knownTeamIds.has(task.teamId)
        ? task.teamId
        : UNASSIGNED_TEAM_KEY;

      if (!accumulator[teamKey]) {
        accumulator[teamKey] = [];
      }

      accumulator[teamKey].push(task);
      return accumulator;
    }, {});
  }, [knownTeamIds, selectedDayTasks]);

  // El endpoint devuelve cargas por equipo, y estas tareas no tienen equipo: la card se
  // arma con la misma fuente que alimenta la columna, asi que aparecen y desaparecen juntas.
  const unassignedLoad = useMemo(() => {
    const unassignedTasks = dayTasksByTeam[UNASSIGNED_TEAM_KEY] || [];

    const loadByCategory = unassignedTasks.reduce((accumulator, task) => {
      const categoryName = getTaskTypeMeta(task, taskTypeLookup)?.categoryName;

      // Sin categoria no suma aca: se recupera como "Sin tipo" por diferencia
      // contra el total al armar las filas del desglose
      if (!categoryName) {
        return accumulator;
      }

      accumulator[categoryName] = (accumulator[categoryName] || 0) + 1;
      return accumulator;
    }, {});

    return { teamId: UNASSIGNED_TEAM_KEY, tasksLoad: unassignedTasks.length, loadByCategory };
  }, [dayTasksByTeam, taskTypeLookup]);

  const bandLoadsByTeamId = useMemo(() => (
    hasUnassignedTasksInSelectedDay
      ? { ...loadsByTeamId, [UNASSIGNED_TEAM_KEY]: unassignedLoad }
      : loadsByTeamId
  ), [hasUnassignedTasksInSelectedDay, loadsByTeamId, unassignedLoad]);

  const dayHours = useMemo(() => {
    const populatedHours = Object.keys(dayBuckets).map(Number).filter((hour) => Number.isInteger(hour));

    if (populatedHours.length === 0) {
      return Array.from(
        { length: DEFAULT_DAY_END_HOUR - DEFAULT_DAY_START_HOUR + 1 },
        (_, index) => DEFAULT_DAY_START_HOUR + index,
      );
    }

    const startHour = Math.max(0, Math.min(DEFAULT_DAY_START_HOUR, ...populatedHours));
    const endHour = Math.min(23, Math.max(DEFAULT_DAY_END_HOUR, ...populatedHours));

    return Array.from({ length: endHour - startHour + 1 }, (_, index) => startHour + index);
  }, [dayBuckets]);

  const rowHeights = useMemo(() => {
    return dayHours.reduce((accumulator, hour) => {
      const maxTasksInColumn = visibleTeams.reduce((maxCount, team) => {
        const currentCount = dayBuckets[hour]?.[team.id]?.length || 0;
        return Math.max(maxCount, currentCount);
      }, 0);

      accumulator[hour] = maxTasksInColumn === 0
        ? EMPTY_DAY_ROW_HEIGHT
        : Math.max(POPULATED_DAY_ROW_MIN_HEIGHT, maxTasksInColumn * 86 + 10);
      return accumulator;
    }, {});
  }, [dayBuckets, dayHours, visibleTeams]);

  const dayHourTaskTotals = useMemo(() => {
    return dayHours.reduce((accumulator, hour) => {
      accumulator[hour] = Object.values(dayBuckets[hour] || {}).reduce((count, teamTasks) => {
        return count + teamTasks.length;
      }, 0);
      return accumulator;
    }, {});
  }, [dayBuckets, dayHours]);

  const dayRows = useMemo(() => {
    if (isGroupedByTeamView) {
      return [];
    }

    const rows = [];
    let currentIndex = 0;

    while (currentIndex < dayHours.length) {
      const hour = dayHours[currentIndex];
      const hasTasksInAnyTeam = (dayHourTaskTotals[hour] || 0) > 0;

      if (hasTasksInAnyTeam) {
        rows.push({ type: 'hour', hour });
        currentIndex += 1;
        continue;
      }

      let endIndex = currentIndex;
      while (
        endIndex + 1 < dayHours.length
        && (dayHourTaskTotals[dayHours[endIndex + 1]] || 0) === 0
      ) {
        endIndex += 1;
      }

      rows.push({
        type: 'gap',
        startHour: hour,
        endHour: dayHours[endIndex],
      });
      currentIndex = endIndex + 1;
    }

    return rows;
  }, [dayHourTaskTotals, dayHours, isGroupedByTeamView]);

  const monthGrid = useMemo(() => getMonthGrid(safeSelectedDate), [safeSelectedDate]);

  const totalDayTasks = useMemo(() => {
    return selectedDayTasks.length;
  }, [selectedDayTasks]);

  useEffect(() => {
    if (!popoverTask) return undefined;

    // Durante el recorrido guiado el detalle lo abre y cierra el propio recorrido: los clics
    // (bloqueados por el velo) y Esc no deben cerrarlo a mitad de un paso.
    const isGuidedTourActive = () => document.body.classList.contains('driver-active');
    const handleOutsideClick = (event) => {
      if (isGuidedTourActive() || event.target.closest('[data-action-menu-portal]')) return;
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setPopoverTask(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !isGuidedTourActive()) {
        setPopoverTask(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [popoverTask]);

  useEffect(() => {
    if (!isDatePickerOpen) return undefined;

    const updateMiniCalendarPosition = () => {
      const trigger = miniCalendarButtonRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      const popupWidth = 344;
      const popupHeight = 336;

      let left = rect.left;
      let top = rect.bottom + margin;

      if (left + popupWidth > window.innerWidth - margin) {
        left = window.innerWidth - popupWidth - margin;
      }

      if (left < margin) {
        left = margin;
      }

      if (top + popupHeight > window.innerHeight - margin) {
        top = rect.top - popupHeight - margin;
      }

      if (top < margin) {
        top = margin;
      }

      setMiniCalendarPosition({ top, left });
    };

    const handleOutsideClick = (event) => {
      const clickedTrigger = miniCalendarButtonRef.current?.contains(event.target);
      const clickedPopup = miniCalendarRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPopup) {
        setIsDatePickerOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsDatePickerOpen(false);
      }
    };

    updateMiniCalendarPosition();
    window.addEventListener('resize', updateMiniCalendarPosition);
    window.addEventListener('scroll', updateMiniCalendarPosition, true);
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', updateMiniCalendarPosition);
      window.removeEventListener('scroll', updateMiniCalendarPosition, true);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDatePickerOpen]);

  useEffect(() => {
    if (mode === 'month') {
      setIsDatePickerOpen(false);
    }
  }, [mode]);

  const openTaskPopover = (task, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const popoverWidth = TASK_POPOVER_WIDTH;
    const margin = TASK_POPOVER_VIEWPORT_MARGIN;
    const desiredPopoverHeight = 640;
    const viewportHeight = window.innerHeight;
    let left = rect.right + margin;
    let top = rect.top;

    if (left + popoverWidth > window.innerWidth - margin) {
      left = rect.left - popoverWidth - margin;
    }

    if (left < margin) left = margin;
    top = Math.max(
      margin,
      Math.min(rect.top, viewportHeight - desiredPopoverHeight - margin),
    );

    const maxHeight = Math.max(260, viewportHeight - top - margin);

    setPopoverPosition({ top, left, maxHeight });
    setPopoverTask(task);
  };

  const handleNavigate = (direction) => {
    const nextDate = new Date(safeSelectedDate);
    if (mode === 'month') {
      nextDate.setMonth(nextDate.getMonth() + direction);
    } else {
      nextDate.setDate(nextDate.getDate() + direction);
    }

    onDateChange?.(nextDate);
  };

  const handleToday = () => {
    onDateChange?.(new Date());
  };

  const handleMiniCalendarChange = (nextDate) => {
    onDateChange?.(ensureDate(nextDate));
    setIsDatePickerOpen(false);
  };

  const syncHorizontalScroll = (sourceElement, targetElement) => {
    if (!sourceElement || !targetElement || isSyncingHorizontalScrollRef.current) {
      return;
    }

    isSyncingHorizontalScrollRef.current = true;
    targetElement.scrollLeft = sourceElement.scrollLeft;

    window.requestAnimationFrame(() => {
      isSyncingHorizontalScrollRef.current = false;
    });
  };

  const updateFloatingScrollbarMetrics = () => {
    const calendarViewport = calendarViewportRef.current;
    const scrollContainer = scrollContainerRef.current;
    const floatingScrollbarTrack = floatingScrollbarTrackRef.current;

    if (!calendarViewport || !scrollContainer) {
      setFloatingScrollbarMetrics((currentMetrics) => {
        if (!currentMetrics.isVisible && currentMetrics.left === 0 && currentMetrics.width === 0) {
          return currentMetrics;
        }

        return { isVisible: false, left: 0, width: 0 };
      });
      return;
    }

    if (floatingScrollbarTrack) {
      floatingScrollbarTrack.style.width = `${scrollContainer.scrollWidth}px`;
    }

    const calendarRect = calendarViewport.getBoundingClientRect();
    const hasHorizontalOverflow = scrollContainer.scrollWidth - scrollContainer.clientWidth > 1;
    const isCalendarVisible = calendarRect.bottom > 0 && calendarRect.top < window.innerHeight;
    const nextMetrics = {
      isVisible: hasHorizontalOverflow && isCalendarVisible,
      left: Math.max(0, calendarRect.left),
      width: Math.max(0, calendarRect.width),
    };

    setFloatingScrollbarMetrics((currentMetrics) => {
      if (
        currentMetrics.isVisible === nextMetrics.isVisible
        && currentMetrics.left === nextMetrics.left
        && currentMetrics.width === nextMetrics.width
      ) {
        return currentMetrics;
      }

      return nextMetrics;
    });
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const floatingScrollbar = floatingScrollbarRef.current;

    if (!scrollContainer || !floatingScrollbar) {
      return undefined;
    }

    const handleMainScroll = () => syncHorizontalScroll(scrollContainer, floatingScrollbar);
    const handleFloatingScroll = () => syncHorizontalScroll(floatingScrollbar, scrollContainer);

    floatingScrollbar.scrollLeft = scrollContainer.scrollLeft;
    scrollContainer.addEventListener('scroll', handleMainScroll, { passive: true });
    floatingScrollbar.addEventListener('scroll', handleFloatingScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleMainScroll);
      floatingScrollbar.removeEventListener('scroll', handleFloatingScroll);
    };
  }, [floatingScrollbarMetrics.isVisible, mode, isGroupedByTeamView, visibleTeams.length]);

  useEffect(() => {
    updateFloatingScrollbarMetrics();

    const handleViewportChange = () => {
      updateFloatingScrollbarMetrics();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && scrollContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateFloatingScrollbarMetrics();
      });
      resizeObserver.observe(scrollContainerRef.current);

      const scrollContent = scrollContainerRef.current.firstElementChild;
      if (scrollContent) {
        resizeObserver.observe(scrollContent);
      }
    }

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      resizeObserver?.disconnect();
    };
  }, [mode, isGroupedByTeamView, visibleTeams.length, dayRows.length, totalDayTasks]);

  const scheduleGridTemplateColumns = `84px repeat(${visibleTeams.length}, minmax(220px, 1fr))`;
  const scheduleGridMinWidth = `${84 + visibleTeams.length * 220}px`;
  const groupedGridTemplateColumns = `repeat(${visibleTeams.length}, minmax(220px, 1fr))`;
  const groupedGridMinWidth = `${Math.max(visibleTeams.length, 1) * 220}px`;

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-md">
        <div className="border-b border-line px-4 py-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div data-tour="cal-date-nav" className={`flex flex-wrap items-center gap-2 ${mode === 'month' ? 'flex-col sm:flex-row' : ''}`}>
              {mode === 'month' ? (
                <div className="order-2 flex w-full items-center gap-2 sm:contents">
                  <button
                    type="button"
                    onClick={handleToday}
                    className="rounded-lg border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content transition-colors hover:bg-line"
                  >
                    Hoy
                  </button>

                  <select
                    value={mode}
                    data-tour="cal-mode"
                    onChange={(event) => onModeChange?.(event.target.value, safeSelectedDate)}
                    className="rounded-lg border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content focus:outline-none focus:ring-2 focus:ring-amber sm:hidden"
                  >
                    <option value="day">Dia</option>
                    <option value="month">Mes</option>
                  </select>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleToday}
                  className="rounded-lg border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content transition-colors hover:bg-line"
                >
                  Hoy
                </button>
              )}

              <div className={mode === 'month'
                ? 'order-1 grid w-full grid-cols-[44px_minmax(0,1fr)_44px] items-center gap-3 sm:order-none sm:flex sm:w-auto sm:items-center sm:gap-0 sm:rounded-lg sm:border sm:border-line sm:bg-surface-2'
                : 'flex items-center rounded-lg border border-line bg-surface-2'}
              >
                <button
                  type="button"
                  onClick={() => handleNavigate(-1)}
                  className={mode === 'month'
                    ? 'flex h-11 w-11 items-center justify-center rounded-lg border border-line bg-surface-2 text-content-muted transition-colors hover:bg-line hover:text-content sm:h-auto sm:w-auto sm:rounded-none sm:border-0 sm:border-r sm:border-line sm:bg-transparent sm:px-3 sm:py-2'
                    : 'border-r border-line px-3 py-2 text-content-muted transition-colors hover:bg-line hover:text-content'}
                  aria-label="Anterior"
                >
                  <CaretLeft size={18} />
                </button>

                {mode === 'month' && (
                  <h2 className="min-w-0 truncate text-center text-lg font-semibold capitalize text-content sm:hidden">
                    {formatHeaderDate(safeSelectedDate, mode)}
                  </h2>
                )}

                <button
                  type="button"
                  onClick={() => handleNavigate(1)}
                  className={mode === 'month'
                    ? 'flex h-11 w-11 items-center justify-center rounded-lg border border-line bg-surface-2 text-content-muted transition-colors hover:bg-line hover:text-content sm:h-auto sm:w-auto sm:rounded-none sm:border-0 sm:bg-transparent sm:px-3 sm:py-2'
                    : 'px-3 py-2 text-content-muted transition-colors hover:bg-line hover:text-content'}
                  aria-label="Siguiente"
                >
                  <CaretRight size={18} />
                </button>
              </div>

              {mode === 'day' && (
                <button
                  type="button"
                  onClick={(event) => {
                    miniCalendarButtonRef.current = event.currentTarget;
                    setIsDatePickerOpen((currentValue) => !currentValue);
                  }}
                  className="inline-flex items-center justify-center rounded-lg border border-line bg-surface-2 p-2 text-content transition-colors hover:bg-line md:hidden"
                  data-tour="cal-date-picker"
                  aria-label={isDatePickerOpen ? 'Ocultar calendario' : 'Abrir calendario'}
                  title={isDatePickerOpen ? 'Ocultar calendario' : 'Abrir calendario'}
                >
                  <CalendarBlank size={18} />
                </button>
              )}

              {mode === 'day' && (
                <select
                  value={mode}
                  data-tour="cal-mode"
                  onChange={(event) => onModeChange?.(event.target.value, safeSelectedDate)}
                  className="rounded-lg border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content focus:outline-none focus:ring-2 focus:ring-amber md:hidden"
                >
                  <option value="day">Dia</option>
                  <option value="month">Mes</option>
                </select>
              )}

              <h2 className={`ml-1 text-lg font-semibold capitalize text-content ${
                mode === 'month' ? 'hidden sm:block' : 'basis-full md:basis-auto'
              }`}>
                {formatHeaderDate(safeSelectedDate, mode)}
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {mode === 'day' && (
                <button
                  type="button"
                  data-tour="cal-layout-toggle"
                  onClick={() => setIsGroupedByTeamView((currentValue) => !currentValue)}
                  className="hidden rounded-lg border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content transition-colors hover:bg-line md:inline-flex"
                >
                  {isGroupedByTeamView ? 'Vista por horarios' : 'Tareas agrupadas por equipos'}
                </button>
              )}

              {mode !== 'month' && (
                <button
                  type="button"
                  onClick={(event) => {
                    miniCalendarButtonRef.current = event.currentTarget;
                    setIsDatePickerOpen((currentValue) => !currentValue);
                  }}
                  className="hidden items-center justify-center rounded-lg border border-line bg-surface-2 p-2 text-content transition-colors hover:bg-line md:inline-flex"
                  data-tour="cal-date-picker"
                  aria-label={isDatePickerOpen ? 'Ocultar calendario' : 'Abrir calendario'}
                  title={isDatePickerOpen ? 'Ocultar calendario' : 'Abrir calendario'}
                >
                  <CalendarBlank size={18} />
                </button>
              )}

              <select
                value={mode}
                data-tour="cal-mode"
                onChange={(event) => onModeChange?.(event.target.value, safeSelectedDate)}
                className={`rounded-lg border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content focus:outline-none focus:ring-2 focus:ring-amber ${
                  mode === 'month' ? 'hidden sm:block' : 'hidden md:block'
                }`}
              >
                <option value="day">Dia</option>
                <option value="month">Mes</option>
              </select>
            </div>
          </div>
        </div>

        <div ref={calendarViewportRef} className="relative">
          {mode === 'day' ? (
            <>
              <div data-tour="cal-day-mobile" className="space-y-3 bg-surface p-3 md:hidden">
                {visibleTeams.map((team) => {
                  const teamTasks = dayTasksByTeam[team.id] || [];
                  const categoryCounts = teamTasks.reduce((counts, task) => {
                    const categoryLabel = getTaskCategoryLabel(task, taskTypeLookup);
                    counts[categoryLabel] = (counts[categoryLabel] || 0) + 1;
                    return counts;
                  }, {});
                  const categories = Object.entries(categoryCounts)
                    .sort(([leftLabel], [rightLabel]) => leftLabel.localeCompare(rightLabel, 'es'));
                  const isOpen = openMobileTeamId === team.id;
                  const taskCountLabel = `${teamTasks.length} ${teamTasks.length === 1 ? 'tarea' : 'tareas'}`;
                  const categoryCountLabel = `${categories.length} ${categories.length === 1 ? 'categoría' : 'categorías'}`;

                  return (
                    <section
                      key={team.id}
                      data-team-with-tasks={teamTasks.length > 0 ? team.id : undefined}
                      className="overflow-hidden rounded-xl border border-line bg-surface-2"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
                        onClick={() => setOpenMobileTeamId((currentTeamId) => (
                          currentTeamId === team.id ? null : team.id
                        ))}
                        aria-expanded={isOpen}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-base font-semibold text-content">{team.name}</span>
                          <span className="mt-1 block text-sm text-content-muted">
                            {taskCountLabel} · {categoryCountLabel}
                          </span>
                        </span>
                        <CaretDown
                          size={20}
                          className={`shrink-0 text-content-muted transition-transform ${isOpen ? 'rotate-180' : '-rotate-90'}`}
                          aria-hidden="true"
                        />
                      </button>

                      {isOpen && (
                        <div className="border-t border-line px-3 py-3">
                          {categories.length > 0 && (
                            <div className="calendar-scrollbar-hidden -mx-3 overflow-x-auto px-3">
                              <div className="flex w-max min-w-full gap-2 pb-1">
                                {categories.map(([categoryLabel, taskCount]) => (
                                  <span
                                    key={categoryLabel}
                                    className="inline-flex shrink-0 items-center gap-2 rounded-full border border-line bg-surface-2 px-3 py-1.5 text-xs font-medium text-content"
                                  >
                                    {categoryLabel}
                                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-line px-1 text-[11px] font-semibold text-content">
                                      {taskCount}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {teamTasks.length > 0 ? (
                            <div className="mt-3 space-y-2">
                              {teamTasks.map((task) => (
                                <TaskCard
                                  key={task.id || `${task.title}-${task.scheduledDate}-${team.id}`}
                                  task={task}
                                  taskTypeLabel={getTaskTypeLabel(task, taskTypeLookup)}
                                  taskTypeLookup={taskTypeLookup}
                                  onClick={(event) => openTaskPopover(task, event)}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className="py-3 text-center text-sm text-content-muted">Sin tareas programadas</p>
                          )}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>

              <div className="hidden md:block">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 text-sm text-content">
                <span>
                  {totalDayTasks > 0
                    ? `${totalDayTasks} ${totalDayTasks === 1 ? 'tarea programada' : 'tareas programadas'}`
                    : 'No hay tareas programadas para el dia seleccionado'}
                </span>

                {/* Con la banda en error el control seria una accion sin efecto */}
                {isTeamLoadsBandVisible && !teamLoadsError && (
                  <button
                    type="button"
                    onClick={() => setIsDetailExpanded((currentValue) => !currentValue)}
                    aria-expanded={isDetailExpanded}
                    aria-controls="team-loads-band"
                    className="inline-flex items-center gap-2 rounded-lg px-1.5 py-1 text-[13px] text-content-muted transition-colors hover:bg-line hover:text-content"
                  >
                    {isDetailExpanded ? 'Ocultar detalle por tipo' : 'Ver detalle por tipo'}
                    <CaretDown
                      size={18}
                      className={`transition-transform ${isDetailExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}
              </div>

              <div
                ref={scrollContainerRef}
                data-tour={isGroupedByTeamView ? 'cal-grouped' : 'cal-schedule'}
                className="calendar-scrollbar-hidden overflow-x-auto"
              >
                <div
                  style={{ minWidth: isGroupedByTeamView ? groupedGridMinWidth : scheduleGridMinWidth }}
                >
                  {isGroupedByTeamView ? (
                    <>
                      {isTeamLoadsBandVisible && (
                        <TeamLoadsBand
                          visibleTeams={visibleTeams}
                          gridTemplateColumns={groupedGridTemplateColumns}
                          loadsByTeamId={bandLoadsByTeamId}
                          isDetailExpanded={isDetailExpanded}
                          loading={isLoadingTeamLoads}
                          error={teamLoadsError}
                          onRetry={refetchTeamLoads}
                        />
                      )}

                      <div
                        className="grid border-b border-line bg-table-head text-xs uppercase tracking-wide text-content-muted"
                        style={{ gridTemplateColumns: groupedGridTemplateColumns }}
                      >
                        {visibleTeams.map((team) => (
                          <div key={team.id} className="min-w-0 overflow-hidden border-r border-line px-4 py-3 font-semibold last:border-r-0">
                            {team.name}
                          </div>
                        ))}
                      </div>

                      <div
                        className="grid bg-surface"
                        style={{ gridTemplateColumns: groupedGridTemplateColumns }}
                      >
                        {visibleTeams.map((team) => {
                          const teamTasks = dayTasksByTeam[team.id] || [];

                          return (
                            <div
                              key={team.id}
                              className="min-w-0 border-r border-line p-2 last:border-r-0"
                            >
                              {teamTasks.length > 0 ? (
                                <div className="min-w-0 space-y-2">
                                  {teamTasks.map((task) => {
                                    const taskTypeLabel = getTaskTypeLabel(task, taskTypeLookup);

                                    return (
                                      <TaskCard
                                        key={task.id || `${task.title}-${task.scheduledDate}-${team.id}`}
                                        task={task}
                                        taskTypeLabel={taskTypeLabel}
                                        taskTypeLookup={taskTypeLookup}
                                        onClick={(event) => openTaskPopover(task, event)}
                                      />
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-line bg-surface-2 px-4 py-6 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-deep">
                                  Sin tareas
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="grid border-b border-line bg-table-head text-xs uppercase tracking-wide text-content-muted"
                        style={{ gridTemplateColumns: scheduleGridTemplateColumns }}
                      >
                        <div
                          className="sticky left-0 z-30 min-w-0 border-l border-r border-line bg-table-head px-4 py-3 font-semibold"
                          style={{ boxShadow: STICKY_HOUR_COLUMN_SHADOW }}
                        >
                          Hora
                        </div>
                        {visibleTeams.map((team) => (
                          <div key={team.id} className="min-w-0 overflow-hidden border-r border-line px-4 py-3 font-semibold last:border-r-0">
                            {team.name}
                          </div>
                        ))}
                      </div>

                      {dayRows.map((row) => {
                        if (row.type === 'gap') {
                          return (
                            <div
                              key={`gap-${row.startHour}-${row.endHour}`}
                              className="grid border-b border-dashed border-line bg-surface-2"
                              style={{ gridTemplateColumns: scheduleGridTemplateColumns, minHeight: `${COLLAPSED_EMPTY_ROW_HEIGHT}px` }}
                            >
                              <div
                                className="sticky left-0 z-20 flex min-w-0 items-center border-l border-r border-line bg-surface-2 px-4 py-2 text-xs font-semibold text-content-muted"
                                style={{ boxShadow: STICKY_HOUR_COLUMN_SHADOW }}
                              >
                                {formatHourRangeLabel(row.startHour, row.endHour)}
                              </div>

                              <div
                                className="flex min-w-0 items-center justify-center border-r border-line px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-deep"
                                style={{ gridColumn: `2 / span ${visibleTeams.length}` }}
                              >
                                <div className="flex w-full min-w-0 items-center gap-3">
                                  <span className="h-px flex-1 border-t border-dashed border-line" />
                                  <span className="shrink-0">Sin tareas</span>
                                  <span className="h-px flex-1 border-t border-dashed border-line" />
                                </div>
                              </div>
                            </div>
                          );
                        }

                        const { hour } = row;

                        return (
                          <div
                            key={hour}
                            className="grid border-b border-line bg-surface"
                            style={{ gridTemplateColumns: scheduleGridTemplateColumns, minHeight: `${rowHeights[hour]}px` }}
                          >
                            <div
                              className="sticky left-0 z-20 min-w-0 border-l border-r border-line bg-surface px-4 py-3 text-sm font-semibold text-content"
                              style={{ boxShadow: STICKY_HOUR_COLUMN_SHADOW }}
                            >
                              {formatHourLabel(hour)}
                            </div>

                            {visibleTeams.map((team) => {
                              const teamTasks = dayBuckets[hour]?.[team.id] || [];
                              const hasTasksInCell = teamTasks.length > 0;

                              return (
                                <div
                                  key={`${hour}-${team.id}`}
                                  className={`min-w-0 overflow-hidden border-r border-line last:border-r-0 ${hasTasksInCell ? 'p-2' : 'px-2 py-1'}`}
                                >
                                  <div className="min-w-0 space-y-2">
                                    {teamTasks.map((task) => {
                                      const taskTypeLabel = getTaskTypeLabel(task, taskTypeLookup);

                                      return (
                                        <TaskCard
                                          key={task.id || `${task.title}-${task.scheduledDate}-${team.id}`}
                                          task={task}
                                          taskTypeLabel={taskTypeLabel}
                                          taskTypeLookup={taskTypeLookup}
                                          onClick={(event) => openTaskPopover(task, event)}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
              </div>
            </>
          ) : (
            <>
              <div data-tour="cal-month" className="md:hidden">
                <div className="grid grid-cols-7 border-b border-line bg-table-head text-center text-[10px] font-semibold uppercase tracking-wide text-content-muted">
                  {WEEKDAY_LABELS.map((label) => (
                    <div key={label} className="py-2">
                      {label}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 border-l border-t border-line">
                  {monthGrid.map((day) => {
                    const dayKey = formatYmd(day);
                    const dayTasks = [...(tasksByMonthDay[dayKey] || [])].sort((leftTask, rightTask) => {
                      return new Date(leftTask.scheduledDate).getTime() - new Date(rightTask.scheduledDate).getTime();
                    });
                    const isCurrentMonth = day.getMonth() === safeSelectedDate.getMonth();
                    const isToday = dayKey === todayKey;
                    const isSelected = dayKey === selectedDayKey;
                    const hiddenTaskCount = Math.max(0, dayTasks.length - 3);

                    return (
                      <div
                        key={dayKey}
                        className={`min-h-[68px] cursor-pointer border-b border-r border-line p-1.5 transition-colors hover:bg-line ${
                          isCurrentMonth ? 'bg-surface' : 'bg-bg'
                        } ${isSelected ? 'ring-1 ring-inset ring-amber' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`${day.getDate()} de ${formatHeaderDate(day, 'day')}, ${dayTasks.length} ${dayTasks.length === 1 ? 'tarea' : 'tareas'}`}
                        onClick={() => onModeChange?.('day', day)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onModeChange?.('day', day);
                          }
                        }}
                      >
                        <span
                          className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold ${
                            isToday
                              ? 'bg-amber text-on-amber'
                              : isCurrentMonth
                                ? 'text-content'
                                : 'text-content-muted'
                          }`}
                        >
                          {day.getDate()}
                        </span>

                        <div className="mt-1.5 space-y-1" aria-label={`${dayTasks.length} tareas`}>
                          {dayTasks.slice(0, 3).map((task) => {
                            const statusColor = getTaskStatusColor(task.status);
                            const title = task.title?.trim() || 'Sin titulo';

                            return (
                              <span
                                key={task.id || `${task.title}-${task.scheduledDate}-${dayKey}`}
                                className="block h-1.5 w-full rounded-full"
                                style={{ backgroundColor: statusColor }}
                                title={`${title} — ${getTaskStatusLabel(task.status)}`}
                                aria-label={`${title}: ${getTaskStatusLabel(task.status)}`}
                              />
                            );
                          })}

                          {hiddenTaskCount > 0 && (
                            <span className="block text-right text-[9px] font-semibold leading-none text-content-muted">
                              +{hiddenTaskCount}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div ref={scrollContainerRef} data-tour="cal-month" className="calendar-scrollbar-hidden hidden overflow-x-auto md:block">
              <div className="min-w-[980px]">
                <div className="grid grid-cols-7 border-b border-line bg-table-head text-xs uppercase tracking-wide text-content-muted">
                  {WEEKDAY_LABELS.map((label) => (
                    <div key={label} className="border-r border-line px-4 py-3 font-semibold last:border-r-0">
                      {label}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7">
                  {monthGrid.map((day) => {
                    const dayKey = formatYmd(day);
                    const dayTasks = [...(tasksByMonthDay[dayKey] || [])].sort((leftTask, rightTask) => {
                      return new Date(leftTask.scheduledDate).getTime() - new Date(rightTask.scheduledDate).getTime();
                    });
                    const isCurrentMonth = day.getMonth() === safeSelectedDate.getMonth();
                    const isToday = dayKey === todayKey;
                    const isSelected = dayKey === selectedDayKey;

                    return (
                      <div
                        key={dayKey}
                        className={`min-h-[170px] cursor-pointer border-b border-r border-line p-2 transition-colors last:border-r-0 ${
                          isCurrentMonth ? 'bg-surface' : 'bg-bg'
                        } ${isSelected ? 'ring-1 ring-inset ring-amber' : ''}`}
                        onClick={() => onModeChange?.('day', day)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                              isToday
                                ? 'bg-amber text-on-amber'
                                : isCurrentMonth
                                  ? 'text-content'
                                  : 'text-content-muted'
                            }`}
                          >
                            {day.getDate()}
                          </span>

                          {dayTasks.length > 0 && (
                            <span className="text-[11px] text-content-muted">
                              {dayTasks.length}
                            </span>
                          )}
                        </div>

                        <div className="mt-2 max-h-[122px] space-y-1 overflow-y-auto pr-1">
                          {dayTasks.map((task) => {
                            const taskTypeLabel = getTaskTypeLabel(task, taskTypeLookup);

                            return (
                              <div
                                key={task.id || `${task.title}-${task.scheduledDate}-${dayKey}`}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <TaskCard
                                  task={task}
                                  variant="month"
                                  taskTypeLabel={taskTypeLabel}
                                  taskTypeLookup={taskTypeLookup}
                                  onClick={(event) => openTaskPopover(task, event)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>
            </>
          )}

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-bg/65">
              <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/95 px-4 py-3 text-sm text-content shadow-lg">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                Consultando tareas...
              </div>
            </div>
          )}
        </div>
      </div>

      {floatingScrollbarMetrics.isVisible && (
        <div
          className="pointer-events-none fixed bottom-0 z-40"
          style={{
            left: `${floatingScrollbarMetrics.left}px`,
            width: `${floatingScrollbarMetrics.width}px`,
          }}
        >
          <div className="pointer-events-auto flex h-5 items-center rounded-t-lg border border-b-0 border-line bg-table-head/95 px-1.5 shadow-lg backdrop-blur-sm">
            <div
              ref={floatingScrollbarRef}
              className="w-full overflow-x-auto overflow-y-hidden"
              data-tour="cal-hscroll"
              aria-label="Desplazamiento horizontal del calendario"
            >
              <div ref={floatingScrollbarTrackRef} className="h-px min-w-full" />
            </div>
          </div>
        </div>
      )}

      {portalTarget && isDatePickerOpen && mode !== 'month' && createPortal(
        <div
          ref={miniCalendarRef}
          className="fixed z-[10002] w-fit max-w-[calc(100vw-16px)] overflow-x-auto rounded-xl border border-line bg-surface-2 p-3 shadow-2xl"
          style={{ top: `${miniCalendarPosition.top}px`, left: `${miniCalendarPosition.left}px` }}
        >
          <DateCalendar
            date={safeSelectedDate}
            onChange={handleMiniCalendarChange}
            locale={es}
            color="#f5b731"
            className="generic-date-range__picker compact-date-picker"
          />
        </div>,
        portalTarget,
      )}

      {portalTarget && popoverTask && createPortal(
        <>
          <div className="fixed inset-0 z-[10000] bg-[var(--app-overlay)]" />
          <div
            ref={popoverRef}
            data-tour="cal-task-detail"
            className="fixed z-[10001] flex w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-2xl"
            style={{
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
              maxHeight: `${popoverPosition.maxHeight || 640}px`
            }}
          >
            <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-4">
              <div className="min-w-0">
                <p className="text-lg font-semibold text-content">
                  {popoverTask.title?.trim() || 'Sin titulo'}
                </p>
                <div className="mt-2 space-y-1 text-sm text-content-muted">
                  <p>
                    {formatTaskTime(popoverTask.scheduledDate)} - {getTaskStatusLabel(popoverTask.status)}
                    {showValidation ? ` - ${getTaskValidationLabel(popoverTask)}` : ''}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <span data-tour="cal-task-actions" className="inline-flex">
                  <ActionMenu
                    options={[
                      ...(onViewTask ? [{
                        label: 'Ver',
                        icon: <Eye size={16} />,
                        onClick: () => { onViewTask(popoverTask); setPopoverTask(null); }
                      }] : []),
                      ...(onEditTask && (!canEditTask || canEditTask(popoverTask)) ? [{
                        label: 'Editar',
                        icon: <PencilSimple size={16} />,
                        onClick: () => { onEditTask(popoverTask, true); setPopoverTask(null); }
                      }] : []),
                      ...(onToggleFixedTeam && taskHasTeam(popoverTask) ? [{
                        label: popoverTask.fixedTeam === true ? 'Desfijar' : 'Fijar',
                        icon: popoverTask.fixedTeam === true ? <PushPinSlash size={16} /> : <PushPin size={16} />,
                        onClick: () => { onToggleFixedTeam(popoverTask); setPopoverTask(null); }
                      }] : []),
                      ...(onDeleteTask && (!canDeleteTask || canDeleteTask(popoverTask)) ? [{
                        label: 'Eliminar',
                        icon: <Trash size={16} />,
                        onClick: () => { onDeleteTask(popoverTask); setPopoverTask(null); }
                      }] : [])
                    ]}
                  />
                </span>

                <button
                  type="button"
                  onClick={() => setPopoverTask(null)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-2 hover:text-content md:hidden"
                  aria-label="Cerrar detalle de tarea"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
              <div className="rounded-lg border border-line bg-bg/50 p-3">
                <p className="text-xs uppercase tracking-wide text-content-muted">Datos del cliente</p>
                <p className="mt-1 text-sm text-content">
                  {popoverTask?.client?.name?.trim() || 'Sin nombre'}
                </p>
                <div className="mt-3 flex items-center gap-2 text-content">
                  <Phone size={16} />
                  <span className="text-xs uppercase tracking-wide text-content-muted">Telefono</span>
                </div>
                <p className="mt-1 text-sm text-content">{popoverTask?.client?.phone || 'Sin telefono'}</p>
                <div className="mt-3 flex items-center gap-2 text-content">
                  <MapPin size={16} />
                  <span className="text-xs uppercase tracking-wide text-content-muted">Coordenadas</span>
                </div>
                <p className="mt-1 text-sm text-content">{formatCoordinates(popoverTask)}</p>
              </div>

              <div className="rounded-lg border border-line bg-bg/50 p-3">
                <p className="text-xs uppercase tracking-wide text-content-muted">Tipo de tarea</p>
                <p className="mt-1 text-sm text-content">
                  {getTaskTypeLabel(popoverTask, taskTypeLookup)}
                </p>
                <div className="mt-3 flex items-center gap-2 text-content">
                  <Clock size={16} />
                  <span className="text-xs uppercase tracking-wide text-content-muted">Descripcion</span>
                </div>
                <div className="mt-1 max-h-56 overflow-y-auto pr-1">
                  <p className="whitespace-pre-wrap text-sm text-content">
                    {popoverTask.description?.trim() || 'Sin descripcion'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>,
        portalTarget,
      )}
    </div>
  );
}
